import { Box, Checkbox, FormControlLabel } from '@mui/material';
import tid from '@/common/application/utils/tid';
import type { OptionType } from '../hook/useERPEquipmentSelector';
import { EquipmentClearableSelect } from './EquipmentClearableSelect';

interface EquipmentFilterSectionProps {
  filteredClasses: OptionType[];
  filteredTypes: OptionType[];
  filteredSizes: OptionType[];
  selectedClass: OptionType | null;
  selectedType: OptionType | null;
  selectedSize: OptionType | null;
  selectedMaxSize: OptionType | null;
  strict: boolean;
  loadingEquipmentFormOptions: boolean;
  loadingEquipments: boolean;
  equipmentSizeTouched: boolean;
  equipmentSizeError: string | false | undefined;
  onClassChange: (value: OptionType | null) => void;
  onTypeChange: (value: OptionType | null) => void;
  onSizeChange: (value: OptionType | null) => void;
  onMaxSizeChange: (value: OptionType | null) => void;
  onStrictChange: (checked: boolean) => void;
}

export function EquipmentFilterSection({
  filteredClasses,
  filteredTypes,
  filteredSizes,
  selectedClass,
  selectedType,
  selectedSize,
  selectedMaxSize,
  strict,
  loadingEquipmentFormOptions,
  loadingEquipments,
  equipmentSizeTouched,
  equipmentSizeError,
  onClassChange,
  onTypeChange,
  onSizeChange,
  onMaxSizeChange,
  onStrictChange
}: EquipmentFilterSectionProps) {
  const filtersLoading = loadingEquipmentFormOptions || loadingEquipments;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 2
        }}
      >
        <EquipmentClearableSelect
          options={filteredClasses}
          value={selectedClass}
          onChange={onClassChange}
          label="Equipment Class"
          placeholder="Equipment Class"
          {...tid('jobs:create:equipment-class-select')}
        />
        <EquipmentClearableSelect
          options={filteredTypes}
          value={selectedType}
          onChange={onTypeChange}
          disabled={filtersLoading}
          label="Equipment Type"
          placeholder="Equipment Type"
          {...tid('jobs:create:equipment-type-select')}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <EquipmentClearableSelect
              required
              options={filteredSizes}
              value={selectedSize}
              onChange={onSizeChange}
              disabled={filtersLoading}
              label="Equipment Size"
              placeholder="Equipment Size"
              error={Boolean(equipmentSizeTouched && equipmentSizeError)}
              helperText={equipmentSizeTouched && equipmentSizeError ? equipmentSizeError : undefined}
              {...tid('jobs:create:equipment-size-select')}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox checked={strict} onChange={(e) => onStrictChange(e.target.checked)} {...tid('jobs:create:strict-checkbox')} />
            }
            label="Strict"
          />
        </Box>
        <EquipmentClearableSelect
          options={filteredSizes}
          value={selectedMaxSize}
          onChange={onMaxSizeChange}
          disabled={filtersLoading}
          label="Equipment Max Size"
          placeholder="Equipment Max Size"
          {...tid('jobs:create:equipment-max-size-select')}
        />
      </Box>
    </Box>
  );
}
